// src/pages/supplier/SupplierAnalyticsPage.jsx
// Phase 8 — M8: Supplier analytics dashboard

import { useState, useEffect } from 'react'

const PINK = '#FF3D7F', ORG = '#FF8500', DARK = '#1A1A2E', GREY = '#9CA3AF', GR = '#F3F4F6'

const api = (path) =>
  fetch(`/api/v1${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(r => r.json())

function KPI({ label, value, sub, color = DARK }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.07)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 900, color }}>{value}</div>
      <div style={{ fontWeight: 600, color: DARK, fontSize: '.85rem', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: '.75rem', color: GREY, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// Tiny SVG sparkline
function Sparkline({ data, color = PINK, height = 48 }) {
  if (!data?.length) return <div style={{ height, background: GR, borderRadius: 8 }} />
  const vals = data.map(d => d.value)
  const min  = Math.min(...vals)
  const max  = Math.max(...vals)
  const range= max - min || 1
  const W = 300, H = height
  const pts  = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W
    const y = H - ((v - min) / range) * (H - 8) - 4
    return `${x},${y}`
  }).join(' ')

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function TrendCard({ label, data, color, format = v => v }) {
  const vals  = (data ?? []).map(d => d.value)
  const total = vals.reduce((a, b) => a + b, 0)
  const last  = vals[vals.length - 1] ?? 0
  const prev  = vals[vals.length - 2] ?? last
  const delta = prev > 0 ? ((last - prev) / prev * 100).toFixed(1) : 0
  const up    = +delta >= 0

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, color: GREY, fontSize: '.82rem' }}>{label}</div>
        <span style={{ background: up ? '#DCFCE7' : '#FEE2E2', color: up ? '#166534' : '#991B1B', padding: '2px 7px', borderRadius: 20, fontSize: '.72rem', fontWeight: 700 }}>
          {up ? '↑' : '↓'} {Math.abs(delta)}%
        </span>
      </div>
      <div style={{ fontWeight: 900, fontSize: '1.3rem', color: DARK, marginBottom: 10 }}>{format(total)}</div>
      <Sparkline data={data} color={color} height={44} />
    </div>
  )
}

function BarChart({ data, labelKey = 'category', valueKey = 'gmv', color = PINK }) {
  if (!data?.length) return <div style={{ color: GREY, fontSize: '.85rem', padding: 12 }}>No data.</div>
  const max = Math.max(...data.map(d => d[valueKey] || 0)) || 1
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((row, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 120, fontSize: '.78rem', color: DARK, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{row[labelKey] ?? '—'}</div>
          <div style={{ flex: 1, height: 14, background: GR, borderRadius: 7, overflow: 'hidden' }}>
            <div style={{ width: `${(row[valueKey] / max) * 100}%`, height: '100%', background: color, borderRadius: 7, transition: 'width .5s' }} />
          </div>
          <div style={{ width: 80, textAlign: 'right', fontWeight: 700, fontSize: '.78rem', color: DARK }}>
            ₹{Number(row[valueKey]).toLocaleString('en-IN')}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function SupplierAnalyticsPage() {
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [days,    setDays]    = useState(30)

  useEffect(() => {
    setLoading(true)
    api(`/supplier/analytics?days=${days}`)
      .then(r => { setData(r.data ?? null); setLoading(false) })
  }, [days])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading analytics…</div>
  if (!data)   return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>No analytics data yet. Snapshots are computed daily.</div>

  const s  = data.summary  ?? {}
  const tr = data.trends   ?? {}
  const tp = data.top_products        ?? []
  const cb = data.category_breakdown  ?? []

  const fmtCurr = v => `₹${Number(v).toLocaleString('en-IN')}`

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display,serif', fontWeight: 800, fontSize: '1.75rem', color: DARK, margin: 0 }}>Analytics</h1>
          <p style={{ color: GREY, margin: '4px 0 0', fontSize: '.88rem' }}>Your performance over the last {days} days</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[7,14,30,90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{ padding: '7px 14px', borderRadius: 10, border: `1.5px solid ${days === d ? PINK : '#E5E7EB'}`, background: days === d ? PINK : '#fff', color: days === d ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '.8rem', cursor: 'pointer' }}>{d}d</button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
        <KPI label="Total GMV"        value={fmtCurr(s.gmv_period ?? 0)}        color={PINK}    sub={`${s.orders_period ?? 0} orders`} />
        <KPI label="New Buyers"       value={s.new_buyers_period ?? 0}           color={ORG}     sub={`${s.active_listings ?? 0} active listings`} />
        <KPI label="Avg Rating"       value={`★ ${Number(s.avg_rating ?? 0).toFixed(2)}`} color="#F59E0B" sub={`${s.oos_listings ?? 0} out-of-stock`} />
      </div>

      {/* Trend charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14, marginBottom: 24 }}>
        <TrendCard label="Daily GMV"          data={tr.gmv}        color={PINK}    format={v => `₹${Number(v).toLocaleString('en-IN')}`} />
        <TrendCard label="Daily Orders"       data={tr.orders}     color={ORG}     format={v => Math.round(v)} />
        <TrendCard label="Avg Review Rating"  data={tr.rating}     color="#F59E0B" format={v => `★ ${Number(v).toFixed(2)}`} />
        <TrendCard label="Conversion Rate %"  data={tr.conversion} color="#6366F1" format={v => `${Number(v).toFixed(1)}%`} />
      </div>

      {/* Top products + Category breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
          <div style={{ fontWeight: 700, color: DARK, marginBottom: 16, fontSize: '.9rem' }}>🏆 Top Products (by GMV)</div>
          {tp.length === 0
            ? <div style={{ color: GREY, fontSize: '.85rem' }}>No data yet.</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {tp.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: i === 0 ? '#FEF3C7' : i === 1 ? GR : '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '.82rem', color: i === 0 ? '#92400E' : GREY, flexShrink: 0 }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: DARK, fontSize: '.85rem' }}>Product #{p.product_id}</div>
                      <div style={{ fontSize: '.75rem', color: GREY }}>{p.units ?? 0} units sold</div>
                    </div>
                    <div style={{ fontWeight: 800, color: PINK, fontSize: '.9rem' }}>₹{Number(p.gmv ?? 0).toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            )
          }
        </div>

        <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
          <div style={{ fontWeight: 700, color: DARK, marginBottom: 16, fontSize: '.9rem' }}>📦 Category Breakdown</div>
          <BarChart data={cb} labelKey="category" valueKey="gmv" color={ORG} />
          {cb.length === 0 && <div style={{ color: GREY, fontSize: '.85rem' }}>No category data yet.</div>}
        </div>
      </div>
    </div>
  )
}
